'use client';

import { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';
import { sharedCustomElements } from './registry';

type CustomElementsMap = Record<string, (props: any) => React.ReactNode>;

interface RichTextContextValue {
  customElements: CustomElementsMap;
  /** Site language code (e.g. 'en', 'de') */
  languageCode: string;
}

const RichTextElementsContext = createContext<RichTextContextValue>({
  customElements: sharedCustomElements,
  languageCode: 'en',
});

export const useRichTextElements = () => useContext(RichTextElementsContext).customElements;

export const useRichTextLanguage = () => useContext(RichTextElementsContext).languageCode;

type Props = PropsWithChildren<{
  overrides?: CustomElementsMap;
  /** Site language code (e.g. 'en', 'de'). Defaults to 'en'. */
  languageCode?: string;
}>;

/**
 * Provides site-specific custom element overrides and language code for RichText.
 * Overrides are spread after shared elements, so matching keys replace defaults.
 * Sites without overrides don't need this provider — shared defaults are used.
 */
export const RichTextElementsProvider: FC<Props> = ({ overrides = {}, languageCode = 'en', children }) => {
  const value = useMemo<RichTextContextValue>(
    () => ({ customElements: { ...sharedCustomElements, ...overrides }, languageCode }),
    [overrides, languageCode],
  );
  return <RichTextElementsContext.Provider value={value}>{children}</RichTextElementsContext.Provider>;
};
